// @ts-expect-error - @sentry/react é instalado em cada app, não no shared (mesmo arranjo
// de components/SentryUserContext.tsx)
import * as Sentry from '@sentry/react'

/**
 * Reporta a falha de um endpoint público de recuperação de conta, sem PII.
 *
 * Existe porque essas telas MENTEM por desenho: elas mostram a mesma mensagem
 * neutra tenha o e-mail sido enviado ou não — é o que impede um estranho de
 * descobrir quais e-mails existem na plataforma. O preço é que, com o `catch {}`
 * vazio que havia antes (#786), um 403 de escopo, um 500 do backend e a rede fora
 * chegavam ao usuário como "enviamos o link" e não deixavam rastro nenhum.
 *
 * Vive no shared porque os portais que têm essas telas (adm e ec) precisam
 * reportar do MESMO jeito: com o motivo separado por status e sem o e-mail. Duas
 * cópias divergiriam na primeira vez que uma delas ganhasse um campo novo.
 *
 * **NÃO exporte isto de `utils/index.ts`.** O barrel é carregado pelos testes do
 * próprio shared, que rodam com o `node_modules` do shared — e `@sentry/react` é
 * instalado só nos apps (mesma situação de `components/SentryUserContext.tsx`).
 * Pôr no barrel derruba a suíte do shared com `Cannot find package '@sentry/react'`,
 * e declarar a dep para consertar invalidaria a entrada `../shared` dos CINCO
 * `package-lock.json` — que registram até as `devDependencies` do shared —,
 * quebrando o `npm ci` estrito do Cloudflare Pages em todos os portais. Por isso o
 * import nos apps é pelo caminho do arquivo, não pelo barrel.
 */

/**
 * Campos que descrevem a falha sem descrever QUEM a sofreu.
 *
 * `code` é do axios (`ERR_NETWORK`, `ECONNABORTED`, `ERR_BAD_REQUEST`…): valor de
 * um conjunto fechado, escrito pela lib, nunca pelo usuário.
 */
const descreveFalha = (error: unknown): { httpStatus: string; code?: string } => ({
  httpStatus: String(
    (error as { response?: { status?: number } })?.response?.status ?? 'sem-resposta',
  ),
  code: (error as { code?: string })?.code,
})

// Só `forgot-password`: os pontos que chamam `/auth/resend-verification` exibem o
// erro ao usuário, então não têm o buraco do `catch {}` que esta função existe para
// fechar. Acrescente o literal quando houver call site — não antes.
export const reportAuthRecoveryFailure = (feature: 'forgot-password', error: unknown): void => {
  const { httpStatus, code } = descreveFalha(error)

  // Erro PRÓPRIO, e não o do axios. Um AxiosError carrega `config.data` — o corpo
  // JSON da request, com o e-mail digitado dentro — além de `request`/`response`.
  // Entregá-lo ao SDK deixaria a redação por conta de quais integrações estão
  // ligadas hoje (e os portais rodam com `sendDefaultPii: true`, que amplia o que
  // o SDK anexa por padrão). Montar o evento com campos escolhidos a mão fecha a
  // porta independentemente disso — e o e-mail é de alguém que pode nem ser
  // usuário da plataforma.
  //
  // O que se perde é o stack do axios, que não diz nada de útil aqui: o valor
  // diagnóstico está no status (403 = o portal não resolve escopo de login; 5xx =
  // falha do backend; sem-resposta = rede/CORS), que vira tag e agrupa sozinho.
  const relato = new Error(
    `${feature} falhou (status=${httpStatus}${code ? `, code=${code}` : ''})`,
  )
  relato.name = 'AuthRecoveryRequestError'

  Sentry.captureException(relato, {
    tags: { feature, http_status: httpStatus },
  })
}
